import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, Radio, Stack, rem } from '@mantine/core'

import { Container } from 'components/atoms/container'
import { StyledLandingWrapper } from 'components/pages/landingPage/index.styled'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'

import useStyles from './index.styles'
import { CarePathOption, CarePathOptionProps } from './option'
import { useDetectSCpage } from 'hooks/useDetectSCpage'

export type Props = {
  heading: string
  description: string
  options: CarePathOptionProps[]
  unsetRatio?: boolean
  useCommonAssess?: boolean
  assessFolderName?: string
  keyID?: string
  onSelect: (v: CarePathOptionProps) => void
} & BoxProps

const CarePathSelection = ({
  heading,
  description,
  unsetRatio,
  useCommonAssess,
  assessFolderName,
  options,
  onSelect,
  keyID = '',
  styles,
  classNames,
  className,
}: Props) => {
  const currentUrl = useCurrentUrl()
  const detectPage = useDetectSCpage(keyID)
  const { isNestle, isSimilac, isVinamilk } = detectPage || {}
  const isMobile = useIsMobile()
  const { classes, cx } = useStyles(
    { unsetRatio, detectPage },
    {
      name: 'CarePathSelection',
      styles,
      classNames,
    },
  )

  return (
    <StyledLandingWrapper
      className="service-connection-page"
      style={{
        minHeight: rem(610),
        marginBottom: 0,
        ...(isSimilac && {
          backgroundColor: '#E6FAF7',
        }),
        ...(isVinamilk && {
          backgroundColor: '#CFFEC8',
          backgroundImage: `url(${CommonGAssets.getAssetPath(
            'vinamilk/selection-bg.png',
          )})`,
        }),
      }}
    >
      <Box className={cx(classes.root, className)}>
        <Container className={classes.container}>
          <Stack className={classes.wrapper} align="stretch">
            {isNestle ? (
              <>
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={CommonGAssets.getAssetPath(
                      isMobile
                        ? 'nestle/selection_banner_mobile.png'
                        : 'nestle/selection_banner_desktop.png',
                    )}
                    loading="lazy"
                    alt="Nestle"
                    className={classes.bannerImg}
                  />
                </Box>
              </>
            ) : (
              <>
                {(heading || description) && (
                  <Stack
                    spacing={rem(8)}
                    align="center"
                    sx={{ textAlign: 'center' }}
                  >
                    {heading && (
                      <Text size="h1" className={classes.heading} as="div">
                        <h1
                          dangerouslySetInnerHTML={{
                            __html: heading,
                          }}
                        />
                      </Text>
                    )}
                    {description && (
                      <Text size="p3" className={classes.description} as="div">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: description,
                          }}
                        />
                      </Text>
                    )}
                  </Stack>
                )}
              </>
            )}

            <Radio.Group
              unstyled
              onChange={(v) => {
                onSelect?.(options.filter((i) => i.groupId === v)[0] || {})
              }}
            >
              <Box className={classes.optionList}>
                {options.map((i, idx) => (
                  <Radio
                    classNames={{
                      root: classes.radioOptionRoot,
                      label: classes.radioOptionLabel,
                      labelWrapper: classes.radioOptionLabelWrapper,
                      inner: classes.radioOptionInner,
                    }}
                    value={i.groupId}
                    label={
                      unsetRatio ? (
                        <Box
                          data-event-category="Service Connection"
                          data-event-action="Select Audience"
                          data-event-label={currentUrl}
                          data-event-audience={i.label}
                          className="item pointer-event-child-none"
                        >
                          <img
                            src={
                              useCommonAssess
                                ? CommonGAssets.getAssetPath(
                                    isMobile
                                      ? `${assessFolderName}/option${
                                          idx + 1
                                        }_mobile.png`
                                      : `${assessFolderName}/option${
                                          idx + 1
                                        }.png`,
                                  )
                                : i.icon
                            }
                            loading="lazy"
                            alt={i.label}
                          />
                        </Box>
                      ) : (
                        <CarePathOption
                          data-event-category="Service Connection"
                          data-event-action="Select Audience"
                          data-event-label={currentUrl}
                          data-event-audience={i.label}
                          className="item pointer-event-child-none"
                          classNames={{
                            root: classes.optionRoot,
                            wrapper: classes.optionWrapper,
                            thumbnail: classes.optionThumbnail,
                            heading: classes.optionHeading,
                          }}
                          key={String(idx)}
                          {...i}
                        />
                      )
                    }
                    key={String(idx)}
                  />
                ))}
              </Box>
            </Radio.Group>
          </Stack>
        </Container>
      </Box>
    </StyledLandingWrapper>
  )
}
export { CarePathSelection }
